import type { FrontendDataType, FrontendFieldInfo, TraceItemData } from '@/lib/field-types'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'

// Convert a raw value to frontend field info
function convertValueToFieldInfo(name: string, value: any): FrontendFieldInfo {
  const fieldInfo: FrontendFieldInfo = {
    name,
    value,
    isOptional: false,
    isArray: false,
    type: inferTypeFromValue(value),
  }

  if (Array.isArray(value)) {
    fieldInfo.isArray = true
    fieldInfo.arrayElementType = value.length > 0 ? inferTypeFromValue(value[0]) : 'unknown'
    // CRITICAL: Always create properties for arrays to enable destructuring
    fieldInfo.properties = value.map((item, index) => convertValueToFieldInfo(`[${index}]`, item))
  } else if (typeof value === 'object' && value !== null) {
    // CRITICAL: Always create properties for objects to enable destructuring
    // This ensures objects get properly destructured in the UI instead of showing as textareas
    fieldInfo.properties = Object.entries(value).map(([key, val]) =>
      convertValueToFieldInfo(key, val)
    )
  }

  return fieldInfo
}

// Infer frontend data type from actual JavaScript value
function inferTypeFromValue(value: any): FrontendDataType {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return 'array'

  const jsType = typeof value
  switch (jsType) {
    case 'string':
      // Check if it looks like a date
      if (isDateString(value)) return 'date'
      return 'string'
    case 'number':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'object':
      return 'object'
    default:
      return 'unknown'
  }
}

// Simple date string detection
function isDateString(value: string): boolean {
  if (typeof value !== 'string') return false
  // Check for ISO date format or common date patterns
  const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/
  return dateRegex.test(value) && !isNaN(Date.parse(value))
}

// Export utility functions for reuse in other converters
export { convertValueToFieldInfo, inferTypeFromValue }

// Convert NodeExecution to TraceItemData
export function convertNodeExecutionToFrontendData(traceItem: NodeExecution): TraceItemData {
  const result: TraceItemData = { nodeExecution: traceItem }

  // Convert inputs
  if (traceItem.inputs?.functionArgs && Object.keys(traceItem.inputs.functionArgs).length > 0) {
    const inputFields = Object.entries(traceItem.inputs.functionArgs).map(([key, value]) =>
      convertValueToFieldInfo(key, value)
    )

    result.inputs = inputFields
  }

  // Convert outputs
  if (traceItem.outputs !== null && traceItem.outputs !== undefined) {
    let outputFields: FrontendFieldInfo[]

    if (Array.isArray(traceItem.outputs)) {
      // Handle array outputs
      outputFields = traceItem.outputs.map((value, index) =>
        convertValueToFieldInfo(`Result ${index + 1}`, value)
      )
    } else if (typeof traceItem.outputs === 'object') {
      // Handle object outputs
      outputFields = Object.entries(traceItem.outputs).map(([key, value]) =>
        convertValueToFieldInfo(key, value)
      )
    } else {
      // Handle primitive outputs
      outputFields = [convertValueToFieldInfo('Result', traceItem.outputs)]
    }

    result.outputs = outputFields
  }

  if (traceItem.outputs?.error) {
    result.error = {
      name: 'message',
      type: 'string',
      value: traceItem.outputs.error,
      isOptional: false,
      isArray: false,
    } satisfies FrontendFieldInfo
  } else if ('error' in traceItem) {
    result.error = {
      name: 'message',
      type: 'string',
      value: traceItem.error,
      isOptional: false,
      isArray: false,
    } satisfies FrontendFieldInfo
  } else if ('failureReason' in traceItem) {
    result.error = {
      name: 'message',
      type: 'string',
      value: traceItem.failureReason,
      isOptional: false,
      isArray: false,
    } satisfies FrontendFieldInfo
  }

  return result
}
